'use client';

import Link from 'next/link';
import { Globe } from 'lucide-react';
import { parseHubIdsFromSystemTags } from '@/components/lookups/HubLinker';
import { useCustomQuery } from '@/lib/hooks/useCustomQuery';
import { contentApi } from '@/lib/api/endpoints';

interface CachedHub {
  id: number;
  title: string;
  slug?: string;
}

interface LinkedHubsProps {
  /** systemTags array containing @hub=<id> entries */
  systemTags?: string[] | null;
  /** Cached hub data from object.data.hubs for optimistic display */
  cachedHubs?: CachedHub[];
}

/**
 * Shows which hubs an object is linked to.
 * Displays cached data immediately, then fetches all hubs in a single batch request
 * returning only slug and title for minimal payload.
 */
export function LinkedHubs({ systemTags, cachedHubs }: LinkedHubsProps) {
  const hubIds = parseHubIdsFromSystemTags(systemTags);

  // Single batch fetch for all hub IDs — only request slug + title
  const { data: freshHubs } = useCustomQuery({
    queryKey: ['content', 'batch-hubs', hubIds],
    queryFn: () =>
      contentApi.getContent({ ids: hubIds, limit: hubIds.length, fields: 'slug,title' }),
    enabled: hubIds.length > 0,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  if (hubIds.length === 0) return null;

  // Build lookup maps from cached and fresh data
  const cachedMap = new Map<number, CachedHub>();
  if (cachedHubs) {
    for (const hub of cachedHubs) {
      cachedMap.set(hub.id, hub);
    }
  }

  const freshMap = new Map<number, { title?: string; slug?: string }>();
  if (freshHubs?.content) {
    for (const item of freshHubs.content) {
      freshMap.set(item.id, { title: item.title, slug: item.slug });
    }
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      {hubIds.map((id) => {
        const fresh = freshMap.get(id);
        const cached = cachedMap.get(id);
        const displayName = fresh?.title || cached?.title || `Hub ${id}`;
        const slug = fresh?.slug || cached?.slug;
        const href = `/content/${slug || id}`;

        return (
          <Link
            key={id}
            href={href}
            className="inline-flex items-center gap-1.5 bg-emerald-50 text-emerald-800 border border-emerald-200 px-2.5 py-1 rounded-md text-xs font-medium hover:bg-emerald-100 transition-colors cursor-pointer"
          >
            <Globe className="w-3 h-3 flex-shrink-0" />
            <span>{displayName}</span>
          </Link>
        );
      })}
    </div>
  );
}
